import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Typeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import usePortAsyncTypeahead from './usePortAsyncTypeahead'; // Import the custom hook

const AsyncTypeaheadSearch = ({
    label,
    handleInputChange,
    handleOnChange,
    error,
    errorMessage,
    value,
    name,
    disabled,
}) => {
    const [selected, setSelected] = useState(value || []);
    const typeaheadRef = useRef(null);
    const lastSyncedValueRef = useRef(null);

    useEffect(() => {
        if (!value || value.length === 0) {
            setSelected([]);
            lastSyncedValueRef.current = null;
            return;
        }
        const valueKey = value[0]?.ulid ?? value[0]?.id ?? value[0]?.name;
        if (valueKey === lastSyncedValueRef.current) {
            return;
        }
        lastSyncedValueRef.current = valueKey;
        setSelected(value);
    }, [value]);

    // Query state not needed; backend provides ordered results
    const { options, isLoading, loadMore, search, hasNext } = usePortAsyncTypeahead(); // Use the custom hook

    // Use options as-is; backend provides sorted order (numeric-first, then A–Z)
    const sortedOptions = options || [];

    const handleInput = useMemo(() => {
        return (query) => {
            search(query);
            handleInputChange && handleInputChange(query);
        };
    }, [search, handleInputChange]);

    const onChangeType = (selectedOptions) => {
        setSelected(selectedOptions);
        handleOnChange(selectedOptions);
    };

    return (
        <>
            <label className='text-neutral-800 mb-1 font-b3 text-start'>{label.name}</label>
            <Typeahead
                ref={typeaheadRef}
                disabled={disabled}
                name={label.name}
                id={label.id}
                labelKey={(option) => (option.name ? `${option.name}` : '')}
                multiple={false}
                options={sortedOptions}
                isLoading={isLoading}
                placeholder={label.placeholder}
                size='sm'
                minLength={0}
                maxResults={sortedOptions.length} // Show all loaded options (no limit)
                filterBy={() => true} // Disable Typeahead's frontend filtering - backend handles filtering
                onInputChange={handleInput}
                onChange={onChangeType}
                selected={selected}
                defaultSelected={value ? value : []}
                renderMenu={(results, menuProps) => {
                    // Always use sortedOptions since backend handles filtering
                    // This ensures all loaded ports are displayed
                    const sortedResults = sortedOptions;

                    return (
                        <Menu {...menuProps}>
                            {sortedResults.map((result, index) => (
                                <MenuItem key={`${result.ulid || result.id || index}`} option={result} position={index}>
                                    {result.name}
                                </MenuItem>
                            ))}
                            {sortedResults?.length === 0 && !isLoading ? (
                                <p key={'noData'} className='mb-0 font-b2 ps-2'>
                                    No Data Found
                                </p>
                            ) : null}
                            {hasNext && (
                                <li style={{ listStyle: 'none', padding: 0 }}>
                                    <button
                                        type="button"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (e.nativeEvent) {
                                                e.nativeEvent.stopImmediatePropagation();
                                            }
                                            if (!isLoading && hasNext) {
                                                loadMore();
                                            }
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (e.nativeEvent) {
                                                e.nativeEvent.stopImmediatePropagation();
                                            }
                                        }}
                                        className={`w-100 text-center font-bold font-b3 text-primary-900 border-0 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        style={{
                                            pointerEvents: isLoading ? 'none' : 'auto',
                                            padding: '8px',
                                            backgroundColor: 'transparent',
                                            outline: 'none'
                                        }}>
                                        {isLoading ? 'Loading...' : 'Load More'}
                                    </button>
                                </li>
                            )}
                        </Menu>
                    );
                }}
            />
            {error && <p className='text-error-700 font-b3 font-regular mt-2'>{errorMessage}</p>}
        </>
    );
};

export default AsyncTypeaheadSearch;
